import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Alert, Intent, Drawer, Position } from "@blueprintjs/core";

// should these be here?
import "normalize.css";
import "@blueprintjs/core/lib/css/blueprint.css";
import "@blueprintjs/icons/lib/css/blueprint-icons.css";
import './App.scss';
import RBush from 'rbush';
import bbox from '@turf/bbox';
import keyBy from 'lodash/keyBy';
import get from 'lodash/get';
import uniq from 'lodash/uniq';

import { schemeTableau10 } from 'd3-scale-chromatic';
import { scaleOrdinal } from 'd3-scale';
import MapBox from './MapBox';
import Bottombar from './Bottombar';
import DataPanel from './DataPanel/index';
import SiteDetails from './SiteDetails/index';
import LayerPanel from './LayerPanel';
import layerListInit from './layers.json';


// ["#4e79a7","#f28e2c","#e15759","#76b7b2","#59a14f","#edc949","#af7aa1","#ff9da7","#9c755f","#bab0ab"]

// TODO do this on backend 
import { timeParse, timeFormat } from 'd3-time-format';
import { timeMonth } from 'd3-time';

const parser = timeParse("%d/%m/%Y");
const barFormatter = timeFormat("time:%Y-%m-%d");

const makeFeatureIdPropMap = fc => keyBy(fc.features.map(f => f.properties), 'id');

const transformTemp = fc => ({
  ...fc,
  features: fc.features.map(f => ({
    ...f,
    properties: {
      ...f.properties,
      SUBMITTED_MONTH: barFormatter(timeMonth.floor(parser(f.properties.SUBMITTED_DATE))),
      NIVO_TIME: Date.parse(timeMonth.floor(parser(f.properties.SUBMITTED_DATE)))
    }
  }))
})


const buildR = (fc) => {
  const tree = new RBush();
  const items = fc.features.map(f => {
    const [minX, minY, maxX, maxY] = bbox(f.geometry);
    return {
      minX, minY, maxX, maxY,
      id: f.properties.id
    }
  })
  // tree.insert(item);
  tree.load(items);
  // TODO clear
  // tree.clear();
  return tree;
  // const result = tree.search({
  //     minX: 40,
  //     minY: 20,
  //     maxX: 80,
  //     maxY: 70
  // });
}

const addColor = (data, color, colorProp) => ({
  ...data,
  features: data.features.map(f => ({
    ...f,
    properties: {
      ...f.properties,
      color: color(f.properties[colorProp])
    }
  }))
});


export const aggProps = ['COST_OF_DEVELOPMENT', 'DWELLINGS_TO_BE_CONSTRUCTED'];
export const colorProps = ['STATUS', 'TYPE_OF_DEVELOPMENT_GROUPING', 'SUBMITTED_MONTH'];


function App() {
  const [bounds, setBounds] = useState();
  const [tree, setTree] = useState();
  const [inView, setInView] = useState([]); // ids in mapview
  const [selection, setSelection] = useState([]); // ids selected
  const parsedUrl = new URL(window.location.href);
  const projectId = parsedUrl.pathname.split('/')[1]
  const [data, setData] = useState({ features: [] });
  const [featurePropMap, setFeaturePropMap] = useState({});
  const [uniquePropsMap, setUniquePropsMapp] = useState({});
  const geocodeRef = useRef();
  // group bys
  const [aggProp, setAggProp] = useState(aggProps[0]);
  const [filterProp, setFilterProp] = useState(undefined);
  const [colorProp, setColorProp] = useState(colorProps[0]);
  const [layerList, setLayerList] = useState(layerListInit);
  const [opacityMap, setOpacityMap] = useState(layerListInit.reduce((acc, cur) => ({ ...acc, [cur.id]: 0 }), {}));

  // const debouncedInView = useDebounce(inView, 500);
  const color = useMemo(
    () => scaleOrdinal(Array.from(new Set(data['features'].map(f => f.properties[colorProp]))), schemeTableau10),
    [data, colorProp]
  );

  useEffect(() => {
    fetch(`https://storage.googleapis.com/city-app/${projectId}.json`)
      .then(r => r.json())
      .then(r => {
        setData(transformTemp(addColor(r, color, colorProp)));
        setFeaturePropMap(makeFeatureIdPropMap(r));
        // get all possible values for chart axes
        setUniquePropsMapp(colorProps.reduce((acc, cur) => ({
          ...acc,
          [cur]: uniq(r.features.map(f => f.properties[cur]))
            .filter(p => p)
            .sort()
        }), {}));
      })
  }, [projectId])

  useEffect(() => {
    setData(data => addColor(data, color, colorProp));
  }, [colorProp]);

  useEffect(() => setTree(buildR(data)), [data])

  useEffect(() => {
    if (!tree) return;
    if (!bounds) return;
    const { lng: minX, lat: minY } = bounds._sw;
    const { lng: maxX, lat: maxY } = bounds._ne;
    const result = tree.search({
      minX, minY, maxX, maxY
    });
    setInView(result.map(r => r.id))
  }, [bounds])


  const mbox = React.useMemo(() => {
    return <MapBox
      center={get(data, 'properties.center')}
      data={data}
      selection={selection}
      setSelection={setSelection}
      setBounds={setBounds}
      layerList={layerList}
      opacityMap={opacityMap}
      geocodeRef={geocodeRef}
    />
  }, [data, selection, layerList, opacityMap])

  return <div className="c__app_root">
    <Drawer
      isOpen={selection.length > 0}
      size={Drawer.SIZE_SMALL}
      icon="info-sign"
      onClose={() => setSelection([])}
      canOutsideClickClose={false}
      enforceFocus={false}
      title="Site details"
      // classes have to set pointer-events: none even with hasBackDrop={false} 
      hasBackdrop={false}
      className={'giraffe-drawer'}
      portalClassName={'giraffe-drawer-portal'}
      position={Position.LEFT}
    >
      {selection.length > 0 &&
        <SiteDetails
          selectedSiteProps={get(featurePropMap, selection[0])}
          featurePropMap
          columns={get(data, 'properties.columns')}
          setSelection={setSelection}
        />
      }
    </Drawer>
    {mbox}
    <div className="panel__data">
      <DataPanel
        data={data}
        inView={inView}
        aggProp={aggProp}
        setAggProp={setAggProp}
        colorProp={colorProp}
        setColorProp={setColorProp}
        setFilterProp={setFilterProp}
        filterProp={filterProp}
        color={color}
        uniquePropsMap={uniquePropsMap}
        geocodeRef={geocodeRef}
      />
    </div>
    <LayerPanel
      layerList={layerList}
      setLayerList={setLayerList}
      opacityMap={opacityMap}
      setOpacityMap={setOpacityMap}
    />
    <Bottombar />
    <Alert isOpen={window.innerWidth < 600} intent={Intent.WARNING} confirmButtonText={undefined}>
      Turn your phone into landscape mode to view this page
    </Alert>
  </div>
}

export default App;
